// composables/useEquipes.ts
import {createEquipeValidator, patchEquipeValidator, updateEquipeValidator} from '~/validators/equipe'
import type {IEquipe} from '~/models/Equipe'
import {Equipe} from '~/models/Equipe'
import type {ISection} from '~/models/Section'
import type {ISectionAssociation} from '~/models/SectionAssociation'
import type {IContactEquipe} from '~/models/ContactEquipe'
import {useApiMonOrga} from '~/composables/api/useApiMonOrga'
import {useAgents} from '~/composables/orga/useAgent'
import {useBreadcrumbLabelsStore} from '~~/stores/breadcrumbLabels'
import type {OrganizationKey} from '~~/errors/ConcurrentChangeError'
import type {MergeRecord} from '~~/utils/organization/concurrentPatch'

type SectionWithAgents = ISection & {
    agents: ISectionAssociation[]
}

export type SectionsMeta = {
    total: number
    page: number
    limit: number
    hasMore: boolean
}

type TFetchEquipesByIdResponse = {
    equipe: IEquipe
    sections: SectionWithAgents[]
    sectionsMeta?: SectionsMeta
}

type FetchApiResponse = {
    dirLib: string
    domLib: string
    ageLib: string
    equLib: string
    equDesc: string
    contacts: IContactEquipe[]
    equMis: string
    sections: SectionWithAgents[]
    sectionsMeta?: SectionsMeta
}

/** Réponse POST création : enveloppe `{ message, equipe, contacts }` (cf. API). */
type EquipeStoreApiResponse = {
    message?: string
    equipe: IEquipe
    contacts?: unknown
}

const mapToEquipe = (raw: IEquipe | FetchApiResponse): Equipe => {
    return new Equipe(raw)
}

export function buildEquipePagePath(payload: {
    dirLib: string
    domLib: string
    ageLib: string
    equLib: string
}): string {
    return `/${[payload.dirLib, payload.domLib, payload.ageLib, payload.equLib]
        .map((segment) => encodeURIComponent(segment))
        .join('/')}`
}

const mapToEquipeWithSection = (data: FetchApiResponse): TFetchEquipesByIdResponse => {
    const {sections, sectionsMeta, ...equipeData} = data
    const sorted = (sections ?? [])
        .slice()
        .sort((a, b) => (a.secOrdre ?? a.secPos ?? 0) - (b.secOrdre ?? b.secPos ?? 0))
    const out: TFetchEquipesByIdResponse = {
        equipe: equipeData as IEquipe,
        sections: sorted,
    }
    if (sectionsMeta) out.sectionsMeta = sectionsMeta
    return out
}

type EquipePageOptions = { page?: number; limit?: number; includeHidden?: boolean }

type AgentLookup = (
    nni: string,
) => Promise<{ agent: { toJSON: () => ISectionAssociation['info'] } } | null>

type EquipeIdentity = {
    dirLib: string
    domLib: string
    ageLib: string
    equLib: string
}

function equipeLookupId(dirLib: string, domLib?: string | null, ageLib?: string | null): string {
    if (domLib && ageLib) {
        return `${dirLib}~${domLib}~${ageLib}~${ageLib}`
    }
    if (domLib) {
        return `${dirLib}~${domLib}~${domLib}~${domLib}`
    }
    return `${dirLib}~${dirLib}~${dirLib}~${dirLib}`
}

async function hasEquipeById(
    dirLib: string,
    domLib?: string | null,
    ageLib?: string | null,
): Promise<string | null> {
    const id = equipeLookupId(dirLib, domLib, ageLib)
    const encodedId = encodeURIComponent(id)
    const data = await useApiMonOrga<{ exists: boolean }>('GET', `equipes/${encodedId}/exists`)
    return data?.exists ? id : null
}

function storedEquipe(data: FetchApiResponse | EquipeStoreApiResponse): Equipe {
    if ('equipe' in data && data.equipe != null) {
        return mapToEquipe(data.equipe)
    }
    return mapToEquipe(data as FetchApiResponse)
}

async function storeEquipe(equipeData: object): Promise<Equipe | null> {
    const payload = await createEquipeValidator.validate(equipeData)
    const data = await useApiMonOrga<FetchApiResponse | EquipeStoreApiResponse>(
        'POST',
        'equipes',
        undefined,
        payload,
    )
    if (!data) return null
    return storedEquipe(data)
}

function equipeFetchUrl(id: string, options?: EquipePageOptions): string {
    const encodedId = encodeURIComponent(id)
    const params = new URLSearchParams()
    if (options?.limit !== undefined) {
        const page = Math.max(1, options.page ?? 1)
        const limit = Math.max(1, Math.min(50, options.limit))
        params.set('page', String(page))
        params.set('limit', String(limit))
    }
    if (options?.includeHidden) {
        params.set('includeHidden', '1')
    }
    const query = params.toString()
    if (query.length > 0) {
        return `equipes/${encodedId}?${query}`
    }
    return `equipes/${encodedId}`
}

function rememberEquipeLabel(id: string, label: string | undefined): void {
    if (!label) return
    try {
        useBreadcrumbLabelsStore().setEquipeLabel(id, label)
    } catch {
        // Store peut être indisponible (SSR / hors composant)
    }
}

async function attachAgent(
    fetchAgentById: AgentLookup,
    agentAssoc: ISectionAssociation,
): Promise<ISectionAssociation> {
    try {
        const agentData = await fetchAgentById(agentAssoc.nni)
        if (!agentData) {
            console.warn(`Agent with NNI ${agentAssoc.nni} not found`)
            return {...agentAssoc}
        }
        return {
            ...agentAssoc,
            info: agentData.agent.toJSON(),
        }
    } catch (error) {
        console.error(`Error fetching agent ${agentAssoc.nni}:`, error)
        return {...agentAssoc}
    }
}

async function attachSectionAgents(
    fetchAgentById: AgentLookup,
    sections: SectionWithAgents[],
): Promise<void> {
    for (const section of sections) {
        section.agents = await Promise.all(
            section.agents.map(function mapAgent(agentAssoc) {
                return attachAgent(fetchAgentById, agentAssoc)
            }),
        )
    }
}

async function loadEquipeById(
    fetchAgentById: AgentLookup,
    identity: EquipeIdentity,
    options?: EquipePageOptions,
): Promise<TFetchEquipesByIdResponse | null> {
    const id = `${identity.dirLib}~${identity.domLib}~${identity.ageLib}~${identity.equLib}`
    const data = await useApiMonOrga<FetchApiResponse>('GET', equipeFetchUrl(id, options))
    if (!data) return null

    const equipe = mapToEquipeWithSection(data)
    const label = data.equLib ?? (equipe.equipe as { equLib?: string }).equLib
    rememberEquipeLabel(id, label)
    await attachSectionAgents(fetchAgentById, equipe.sections)
    return equipe
}

async function updateEquipe(id: string, equipeData: object): Promise<Equipe | null> {
    const payload = await updateEquipeValidator.validate(equipeData)
    const encodedId = encodeURIComponent(id)
    const data = await useApiMonOrga<FetchApiResponse>('PUT', `equipes/${encodedId}`, undefined, payload)
    return data ? mapToEquipe(data) : null
}

async function patchEquipe(
    id: string,
    partialData: object,
    base?: MergeRecord,
): Promise<(Equipe & { currentKey?: OrganizationKey | null }) | null> {
    const changes = await patchEquipeValidator.validate(partialData)
    const encodedId = encodeURIComponent(id)
    const payload = base === undefined ? changes : {changes, base}
    const data = await useApiMonOrga<FetchApiResponse & { currentKey?: OrganizationKey | null }>(
        'PATCH',
        `equipes/${encodedId}`,
        undefined,
        payload,
    )
    if (!data) return null
    return Object.assign(mapToEquipe(data), {currentKey: data.currentKey ?? null})
}

async function deleteEquipe(id: string): Promise<boolean> {
    try {
        const encodedId = encodeURIComponent(id)
        await useApiMonOrga<unknown>('DELETE', `equipes/${encodedId}`)
        return true
    } catch (error) {
        console.error(`Failed to delete equipe with ID ${id}`, error)
        return false
    }
}

export function useEquipes() {
    const {fetchAgentById} = useAgents()

    function fetchEquipeById(
        dirLib: string,
        domLib: string,
        ageLib: string,
        equLib: string,
        options?: EquipePageOptions,
    ): Promise<TFetchEquipesByIdResponse | null> {
        return loadEquipeById(fetchAgentById, {dirLib, domLib, ageLib, equLib}, options)
    }

    return {
        storeEquipe,
        fetchEquipeById,
        updateEquipe,
        patchEquipe,
        deleteEquipe,
        hasEquipeById,
    }
}
